import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { IconClose } from '@belong/icons';
import { Image, Text, VideoPlayer } from '@belong/ui';
import clsx from 'clsx';
import { MediaModel, UnitListingDetailsModel } from 'src/api/models';
import { FavoriteButton } from 'src/features/listing-page/components/favorite-button/favorite-button';
import { HomeTagProps } from 'src/features/listing-page/components/home-tag/home-tag';
import { getNormalizedFavoritesByHomeId } from 'src/features/listing-page/utils';
import { useResidentFavorites } from 'src/features/residents';
import { useModal } from 'src/hooks/useModal';
import {
  getMediaFeatures,
  processHomeFeatures,
} from '../../home-features/utils';
import { GalleryCarousel } from '../../gallery/gallery-carousel';
import { getMediaSectionNames } from '../../../utils';
import styles from '../cover.module.css';
import { COVER_STRINGS } from '../strings';

const HomeTag = dynamic<HomeTagProps>(
  () => import('src/features/listing-page/components/home-tag/home-tag'),
  {
    ssr: false,
  },
);

type PhotosProps = {
  home: UnitListingDetailsModel;
  isPreview: boolean;
};

export function Photos({ home, isPreview }: PhotosProps) {
  const [showVideopreview, setVideoPreview] = useState(true);
  const [showBannerImageFullScreen, openBannerImage, closeBannerImage] =
    useModal(false);
  const { favorites } = useResidentFavorites();
  const normalizedFavoritesByHomeId = getNormalizedFavoritesByHomeId(
    favorites || [],
  );

  const { is3DTourReady, isPhotoReady, guidedTour } = home;
  // `!` on `listingInfo`/`basicInfo`: this tab is only mounted from the house
  // page's `Cover`, and it already destructured both unconditionally.
  const { isAvailableForRent, isDemoUnit, isComingSoon, availabilityStatus } =
    home.listingInfo!;
  const { bannerImageUrl, mediaAvailable } = home.basicInfo!;

  const showGuidedTour =
    !!mediaAvailable?.includes('GuidedTour') &&
    guidedTour?.url &&
    showVideopreview;

  const { mediaSectionNames } = useMemo(
    () => getMediaSectionNames(home),
    [home],
  );

  const categoryToDisplayFeats = useMemo(
    () => processHomeFeatures(home),
    [home],
  );

  const mediaFeaturesSections = useMemo(
    () =>
      Object.keys(categoryToDisplayFeats).reduce<{ media: MediaModel[] }[]>(
        (acc, category) => {
          const featuresWithMedia = getMediaFeatures(
            categoryToDisplayFeats,
            category,
          );
          if (featuresWithMedia.length > 0) {
            acc.push({
              media: featuresWithMedia.flatMap((f) =>
                f.media.map((m) => ({ ...m })),
              ),
            });
          }
          return acc;
        },
        [],
      ),
    [categoryToDisplayFeats],
  );

  const sortedMedia = useMemo(() => {
    const dedupKey = (url: string) => {
      try {
        return new URL(url).pathname;
      } catch {
        // `!`: `split` always yields at least one element.
        return url.split('?')[0]!;
      }
    };
    const roomsMedia = [...mediaSectionNames, ...mediaFeaturesSections].flatMap(
      (s) =>
        [...s.media]
          .filter((m) => !('mediaType' in m) || m.mediaType === 'Image')
          // `!`: `sortOrder` is optional on `MediaModel` only; asserting keeps
          // the comparator's existing behaviour for media that lack one.
          .sort((a, b) => a.sortOrder! - b.sortOrder!)
          .map((m) => m.url),
    );
    const seen = new Set<string>();
    // Explicit predicate: the body has several returns, so TS can't infer one,
    // and `GalleryCarousel` wants plain `string`s.
    return [bannerImageUrl, ...roomsMedia].filter((url): url is string => {
      if (!url) return false;
      const key = dedupKey(url);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [bannerImageUrl, mediaSectionNames, mediaFeaturesSections]);

  return (
    <div className="h-full bg-black">
      <div className="container relative">
        <div className="absolute top-lg">
          <HomeTag
            availabilityStatus={availabilityStatus}
            isAvailableForRent={isAvailableForRent}
            isPhotoReady={isPhotoReady}
            is3DTourReady={is3DTourReady}
            isDemoUnit={isDemoUnit}
            isComingSoon={isComingSoon}
          />
        </div>
      </div>

      <div className="absolute right-sm top-sm flex lg:hidden">
        <FavoriteButton
          homeId={home.basicInfo!.unitId!}
          isFavorite={!!normalizedFavoritesByHomeId[home.basicInfo!.unitId!]}
          key={home.basicInfo!.unitId}
        />
      </div>

      {(isPreview || isAvailableForRent || isComingSoon) && showGuidedTour && (
        <div className="pointer-events-none absolute flex size-full items-center justify-center">
          <div className="container relative h-full">
            <div
              className={clsx(
                'pointer-events-none absolute bottom-3xl right-sm lg:bottom-4xl lg:right-0',
                styles.videoPreview,
              )}
            >
              <button
                type="button"
                aria-label="Close video preview"
                className="pointer-events-auto absolute z-10 cursor-pointer rounded-full border-4 border-solid border-green bg-white p-2xs text-green hover:border-dark-green"
                style={{ top: -15, right: -15 }}
                onClick={() => setVideoPreview(false)}
              >
                <IconClose className="size-2sm cursor-pointer" />
              </button>
              <div
                className="pointer-events-none absolute left-0 top-0 z-10 flex w-full items-end justify-center rounded"
                style={{
                  height: '96%',
                  background:
                    'linear-gradient( 180deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.25) 58.97%, rgba(19, 19, 19, 0.57) 100%, rgba(0, 0, 0, 0.5) 100%)',
                }}
              >
                <div className="mb-xs flex items-center gap-xs px-md text-center lg:mb-sm">
                  <div className="hidden size-md lg:block">
                    <Image src="/icons/video_camera.svg" alt="" />
                  </div>
                  <Text
                    fontWeight="semibold"
                    className="text-p1 text-white lg:text-body"
                  >
                    {COVER_STRINGS.bringToLife}
                  </Text>
                </div>
              </div>
              <VideoPlayer
                playsInline
                // `!`: `showGuidedTour` above is false unless `guidedTour.url` is set.
                videoSrc={guidedTour!.url!}
                posterSrc=""
                showPlayOverlay={false}
                videoClassName="border-2 border-solid border-green shadow rounded hover:border-dark-green pointer-events-auto"
              />
            </div>
          </div>
        </div>
      )}
      {bannerImageUrl && (
        <Image
          src={bannerImageUrl}
          className={clsx('object-cover', {
            'cursor-pointer': isPreview || isAvailableForRent || isComingSoon,
          })}
          alt=""
          onClick={() => {
            if (isPreview || isAvailableForRent || isComingSoon) {
              openBannerImage();
            }
          }}
        />
      )}
      {showBannerImageFullScreen && (
        <GalleryCarousel media={sortedMedia} onDismiss={closeBannerImage} />
      )}
    </div>
  );
}
